import { useState, useCallback, useEffect } from 'react';
import { showError } from '@/lib/dialogHelper';
import i18n from '@/i18n';
import CustomCollectionsServiceNew from '@/services/CustomCollectionsServiceNew';
import { dispatchCollectionsChanged } from '@/lib/collectionsEvents';

const makeDraft = () => ({
  name: '',
  type: 'string',
  required: false,
  filterpresent: false,
  lookupCollectionId: '',
  lookupCollectionName: '',
  lookupLinkField: '',
  lookupDisplayField: ''
});

const buildInitialFormState = ({
  initialName = '',
  initialFields = [],
  initialVisibleInMenu = true,
  initialShowInParentCollection = true,
} = {}) => ({
  name: initialName,
  visibleInMenu: initialVisibleInMenu,
  sortField: 'created',
  sortDirection: 'desc',
  showInParentCollection: initialShowInParentCollection,
  fields: initialFields,
});

export default function useCollectionFormData(options = {}) {
  const {
    initialName = '',
    initialFields = [],
    initialVisibleInMenu = true,
    initialShowInParentCollection = true,
    collectionId = null,
    onSuccess,
    onError,
  } = options;

  const [formData, setFormData] = useState(
    buildInitialFormState({ initialName, initialFields, initialVisibleInMenu, initialShowInParentCollection })
  );

  const [draftField, setDraftField] = useState(makeDraft);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(!!collectionId);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    // console.log(`TCL -> useCollectionFormData.js:51 -> useCollectionFormData -> name, value, type, checked:`, name, value, type, checked)
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    
  }, []);

  const handleDraftFieldChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setDraftField((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }, []);

  const addField = useCallback(() => {
    const fieldName = draftField.name.trim();
    if (!fieldName) return;

    if (draftField.type === 'lookup') {
      if (!draftField.lookupCollectionId) {
        showError(i18n.t('customCollections.form.validation.selectLookupCollection'));
        return;
      }
      if (!draftField.lookupLinkField) {
        showError(i18n.t('customCollections.form.validation.selectLookupLinkField'));
        return;
      }
      if (!draftField.lookupDisplayField) {
        showError(i18n.t('customCollections.form.validation.selectLookupDisplayField'));
        return;
      }
    }

    const exists = formData.fields.some(
      (field) => field.name.toLowerCase() === fieldName.toLowerCase()
    );
    if (exists) {
      showError(i18n.t('customCollections.form.validation.fieldNameExists'));
      return;
    }

    const newField = {
      id: Date.now(),
      name: fieldName,
      type: draftField.type,
      required: !!draftField.required,
      filterpresent: !!draftField.filterpresent,
      ...(draftField.type === 'lookup'
        ? {
            lookupCollectionId: draftField.lookupCollectionId,
            lookupCollectionName: draftField.lookupCollectionName || '',
            lookupLinkField: draftField.lookupLinkField,
            lookupDisplayField: draftField.lookupDisplayField || ''
          }
        : {})
    };

    setFormData((prev) => ({ ...prev, fields: [...prev.fields, newField] }));
    setDraftField(makeDraft());
  }, [draftField, formData.fields]);

  const updateField = useCallback((fieldId, updates) => {
    setFormData((prev) => ({
      ...prev,
      fields: prev.fields.map((field) => {
        if (field.id !== fieldId) return field;

        const next = { ...field, ...updates };

        if (next.type === 'lookup') {
          const lookupCollectionId = updates.lookupCollectionId ?? field.lookupCollectionId ?? '';
          const lookupCollectionName = updates.lookupCollectionName ?? field.lookupCollectionName ?? '';
          const lookupLinkField = updates.lookupLinkField ?? field.lookupLinkField ?? '';
          const lookupDisplayField = updates.lookupDisplayField ?? field.lookupDisplayField ?? '';

          next.lookupCollectionId = lookupCollectionId;
          next.lookupCollectionName = lookupCollectionName;
          next.lookupLinkField = lookupLinkField;
          next.lookupDisplayField = lookupDisplayField;
        } else {
          delete next.lookupCollectionId;
          delete next.lookupCollectionName;
          delete next.lookupLinkField;
          delete next.lookupDisplayField;
        }

        return next;
      }),
    }));
  }, []);

  const removeField = useCallback((fieldId) => {
    setFormData((prev) => ({
      ...prev,
      fields: prev.fields.filter((field) => field.id !== fieldId),
    }));
  }, []);

  useEffect(() => {
    let active = true;

    const loadCollections = async () => {
      const result = await CustomCollectionsServiceNew.getCustomCollectionsWithFilters({ limit: 200 });
      if (!active) return;

      if (result.error) {
        console.error('Failed to load collections for lookup fields:', result.error);
        return;
      }

      const mapped = (result.data || []).map((col) => ({
        id: col._id || col.id,
        name: col.name,
        fields: col.fields || [],
      }));
      setCollections(mapped);
    };

    loadCollections();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!collections.length || !formData.fields || formData.fields.length === 0) return;

    let changed = false;
    const updatedFields = formData.fields.map((field) => {
      if (field.type !== 'lookup') return field;

      const matchedCollection = collections.find(
        (col) => col.id === field.lookupCollectionId || col.name === field.lookupCollectionName
      );

      const resolvedCollectionId = field.lookupCollectionId || matchedCollection?.id || '';
      const resolvedCollectionName = field.lookupCollectionName || matchedCollection?.name || '';
      const resolvedLinkField = field.lookupLinkField || field.lookup?.linkField || field.lookup?.collectionField || '';
      const resolvedDisplayField = field.lookupDisplayField || field.lookup?.displayField || '';

      if (
        resolvedCollectionId === field.lookupCollectionId &&
        resolvedCollectionName === field.lookupCollectionName &&
        resolvedLinkField === field.lookupLinkField &&
        resolvedDisplayField === field.lookupDisplayField
      ) {
        return field;
      }

      changed = true;
      return {
        ...field,
        lookupCollectionId: resolvedCollectionId,
        lookupCollectionName: resolvedCollectionName,
        lookupLinkField: resolvedLinkField,
        lookupDisplayField: resolvedDisplayField,
        lookup: {
          collectionId: resolvedCollectionId,
          collectionName: resolvedCollectionName,
          collectionField: resolvedLinkField,
          linkField: resolvedLinkField,
          displayField: resolvedDisplayField,
        },
      };
    });

    if (changed) {
      setFormData((prev) => ({ ...prev, fields: updatedFields }));
    }
  }, [collections, formData.fields]);

  useEffect(() => {
    if (!collectionId) return;

    let active = true;

    const fetchCollectionDetails = async () => {
      try {
        setLoading(true);
        const { data, error } = await CustomCollectionsServiceNew.getCustomCollectionById(collectionId);

        if (!active) return;

        if (error) {
          console.error('Error fetching collection details:', error);
          showError(error);
          return;
        }

        if (!data) {
          showError(i18n.t('customCollections.form.validation.collectionNotFound'));
          return;
        }

        const processedData = {
          name: data.name || '',
          visibleInMenu: !!data.visibleInMenu,
          sortField: data.sortField || 'created',
          sortDirection: data.sortDirection || 'desc',
          showInParentCollection: !!data.showInParentCollection,
          fields: (data.fields || []).map((field, index) => ({
            id: field.id || Date.now() + index,
            name: field.name || '',
            type: field.type || 'string',
            required: field.required || false,
            filterpresent: field.filterpresent || false,
            lookupCollectionId: field.lookupCollectionId || field.lookup?.collectionId || '',
            lookupCollectionName: field.lookupCollectionName || field.lookup?.collectionName || '',
            lookupLinkField: field.lookupLinkField || field.lookup?.linkField || field.lookup?.collectionField || '',
            lookupDisplayField: field.lookupDisplayField || field.lookup?.displayField || '',
            isNew: false,
          })),
        };

        setFormData(processedData);
      } catch (err) {
        if (!active) return;
        console.error('Unexpected error fetching collection:', err);
        showError(i18n.t('customCollections.form.validation.unexpectedFetchError'));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchCollectionDetails();

    return () => {
      active = false;
    };
  }, [collectionId]);

  const normalizeFields = useCallback((fields) => {
    return (fields || []).map((field) => {
      const base = {
        id: field.id,
        name: field.name,
        type: field.type,
        required: !!field.required,
        filterpresent: !!field.filterpresent,
      };

      if (field.type !== 'lookup') return base;

      const targetCollection = collections.find((col) => col.id === (field.lookupCollectionId || field.lookup?.collectionId));

      return {
        ...base,
        lookupCollectionId: field.lookupCollectionId || field.lookup?.collectionId || '',
        lookupCollectionName: field.lookupCollectionName || field.lookup?.collectionName || targetCollection?.name || '',
        lookupLinkField: field.lookupLinkField || field.lookup?.linkField || field.lookup?.collectionField || '',
        lookupDisplayField: field.lookupDisplayField || field.lookup?.displayField || '',
      };
    });
  }, [collections]);

  const handleSubmit = useCallback(async (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }

    setLoading(true);

    try {
      const missingLookup = formData.fields.find((field) =>
        field.type === 'lookup' && (
          (!field.lookupCollectionId && !field.lookup?.collectionId) ||
          (!field.lookupLinkField && !field.lookup?.linkField && !field.lookup?.collectionField)
        )
      );

      if (missingLookup) {
        showError(i18n.t('customCollections.form.validation.lookupRequiresCollectionAndField', { name: missingLookup.name }));
        return;
      }

      const payload = {
        name: formData.name,
        visibleInMenu: !!formData.visibleInMenu,
        sortField: formData.sortField || 'created',
        sortDirection: formData.sortDirection || 'desc',
        showInParentCollection: formData.showInParentCollection || false,
        fields: normalizeFields(formData.fields),
      };

      const mode = collectionId ? 'edit' : 'add';
      const actionLabel = mode === 'edit' ? 'update' : 'create';
      const submit = mode === 'edit'
        ? () => CustomCollectionsServiceNew.updateCustomCollection(collectionId, payload)
        : () => CustomCollectionsServiceNew.createCustomCollection(payload);

      const { success, error } = await submit();

      if (success) {
        dispatchCollectionsChanged();
        onSuccess?.({ formData, payload, mode });
      } else if (error) {
        showError(error || i18n.t(`customCollections.form.validation.submitFailed.${actionLabel}`));
        onError?.(error);
      }
    } catch (err) {
      const messageContext = collectionId ? 'updating' : 'creating';
      console.error(`Unexpected error during collection ${messageContext}:`, err);
      showError(i18n.t(`customCollections.form.validation.unexpectedSubmit.${collectionId ? 'update' : 'create'}`));
      onError?.(err);
    } finally {
      setLoading(false);
    }
  }, [collectionId, formData, normalizeFields, onError, onSuccess]);

  const getFieldTypeLabel = useCallback((type) => {
    if (type === 'integer') return i18n.t('customCollections.form.fieldTypes.integer');
    if (type === 'lookup') return i18n.t('customCollections.form.fieldTypes.lookup');
    if (type === 'editor') return i18n.t('customCollections.form.fieldTypes.editor');
    return i18n.t('customCollections.form.fieldTypes.string');
  }, []);

  return {
    formData,
    setFormData,
    draftField,
    handleInputChange,
    handleDraftFieldChange,
    addField,
    updateField,
    removeField,
    collections,
    loading,
    setLoading,
    normalizeFields,
    handleSubmit,
    getFieldTypeLabel,
  };
}
